import { FC, memo, useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Popover, SvgIcon, Tooltip } from '@/base-components';
import { useThemeToken } from '@/hooks';
import type { RootState } from '@@store/index';
import type { PopoverPropsType, SvgIconPropsType, TooltipPropsType } from '@/base-components';
import './index.less';

interface IconBtnPropsType extends Omit<SvgIconPropsType, 'name' | 'className' | 'width' | 'height' | 'onClick' | 'onHover' | 'onLeave' | 'aria-label'> {
    hoverTip?: string;
    activeMenu?: React.ReactNode;
    isMenuOpen?: boolean;
    hasHoverTip?: boolean;
    color?: string;
    iconName?: SvgIconPropsType['name'];
    iconWidth?: SvgIconPropsType['width'],
    iconHeight?: SvgIconPropsType['height'],
    tipPlacement?: TooltipPropsType['placement'];
    menuPlacement?: PopoverPropsType['placement'];
    menuArrow?: PopoverPropsType['arrow'];
    tipArrow?: TooltipPropsType['arrow'];
    btnClassName?: string;
    menuClassName?: string;
    children?: React.ReactNode;
    type?: 'text' | 'icon';
    getTipPopupContainer?: any;
    getMenuPopupContainer?: any;
    // eslint-disable-next-line no-unused-vars
    onClick?: (isOpen: boolean, e?: React.MouseEvent<HTMLButtonElement>) => void;
    // eslint-disable-next-line no-unused-vars
    onMenuOpenChange?: (isOpen: boolean) => void;
    'aria-label'?: string;
}

const MenuButton: FC<IconBtnPropsType> = memo((props) => {
    const curTheme = useSelector((state: RootState) => state.theme);

    const { colorText } = useThemeToken();

    const {
        iconName,
        iconWidth,
        iconHeight,
        hasHoverTip = true,
        activeMenu = null,
        isMenuOpen = false,
        hoverTip = '提示',
        color = colorText,
        btnClassName = '',
        menuClassName = '',
        tipPlacement = 'left',
        menuPlacement = 'right',
        menuArrow = false,
        tipArrow = false,
        onClick = null,
        onMenuOpenChange = () => {},
        getTipPopupContainer,
        getMenuPopupContainer,
        disabled,
        type,
        children,
        'aria-label': ariaLabel,
        ...rest
    } = props;

    const [ isHover, setIsHover ] = useState(false);
    const [ isActive, setIsActive ] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const triggerButtonRef = useRef<HTMLButtonElement>(null);

    const btnTypeClassName = useMemo(() => {
        switch (type) {
            case 'icon':
                return `base-icon-btn ${curTheme}-base-icon-btn ${isActive ? `${curTheme}-base-icon-btn-active` : ''}`;
            case 'text':
                return `base-text-btn ${curTheme}-base-text-btn ${isActive ? `${curTheme}-base-text-btn-active` : ''}`;
            default:
                return '';
        }
    }, [ type, curTheme, isActive ]);

    const onBtnHoverChange = (isHover) => {
        setIsHover(isHover && !isActive);
    };

    const onBtnActiveChange = (isActive, e?: any) => {
        isHover && setIsHover(false);
        setIsActive(isActive && !!activeMenu);
        onClick && onClick(isActive && !!activeMenu, e);
        onMenuOpenChange(isActive && !!activeMenu);
    };

    useEffect(() => {
        setIsActive(isMenuOpen);
    }, [ isMenuOpen ]);

    // 面板打开时，自动聚焦到第一个可聚焦元素
    useEffect(() => {
        if (isActive && menuRef.current) {
            const timeout = setTimeout(() => {
                const firstFocusable = menuRef.current?.querySelector<HTMLElement>('[tabindex="0"]:not([disabled]), button:not([disabled]), [role="menuitem"]:not([aria-disabled="true"])');
                if (firstFocusable) {
                    firstFocusable.focus();
                }
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [ isActive ]);

    return (
        <Tooltip
            classNames={{
                root: 'menuButton-tooltip'
            }}
            title={hoverTip}
            placement={tipPlacement}
            open={hasHoverTip && isHover && !!hoverTip}
            getPopupContainer={getTipPopupContainer}
            arrow={tipArrow}
        >
            <Popover
                classNames={{
                    root: `${menuClassName}`
                }}
                trigger={[ 'click' ]}
                placement={menuPlacement}
                arrow={menuArrow}
                content={<div ref={menuRef} style={{ display: 'contents' }}>{activeMenu}</div>}
                open={!!activeMenu && isActive}
                getPopupContainer={getMenuPopupContainer}
                onOpenChange={(open) => {
                    onBtnActiveChange(open);
                }}
            >
                <div
                    onMouseLeave={onBtnHoverChange.bind(null, false)}
                    onMouseEnter={onBtnHoverChange.bind(null, true)}
                >
                    <button
                        ref={triggerButtonRef}
                        type="button"
                        onClick={(e) => {
                            onBtnActiveChange(!isActive, e);
                        }}
                        aria-label={ariaLabel || hoverTip}
                        aria-expanded={isActive}
                        aria-haspopup="menu"
                        style={{
                            position: 'relative',
                            padding: 0,
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer'
                        }}
                    >
                        <SvgIcon
                            name={iconName}
                            color={color}
                            width={iconWidth}
                            height={iconHeight}
                            className={`cursor-pointer ${btnTypeClassName} ${btnClassName} ${isMenuOpen ? btnClassName + '-active' : ''}`}
                            disabled={disabled}
                            {...rest}
                        >
                            {children}
                        </SvgIcon>
                    </button>
                </div>
            </Popover>
        </Tooltip>
    );
});

export default MenuButton;
